/**
 * Blog content — single source of truth for all articles.
 * Each `body` is structured into `sections` so the renderer enforces
 * consistent typography (H2/H3, paragraphs, lists, links) without
 * embedding raw HTML.
 */

export const ARTICLES = [
  {
    slug: "best-curtain-styles-dubai-modern-apartments",
    title: "Best Curtain Styles in Dubai for Modern Apartments",
    excerpt:
      "From ripple fold to sheers, here are the curtain styles that suit modern Dubai apartments — and how to pair them room by room.",
    category: "Curtains",
    date: "2026-04-22",
    readTime: "5 min read",
    image: "/services/Ripplefold-curtains-timefurniture.webp",
    imageAlt:
      "Ripple fold curtains in a modern Dubai apartment by Time Furniture",
    metaTitle:
      "Best Curtain Styles in Dubai for Modern Apartments | Time Furniture",
    metaDescription:
      "A practical guide to the best curtain styles for modern Dubai apartments — ripple fold, pinch pleat, sheers, blackouts and motorized options, with room-by-room picks.",
    sections: [
      {
        type: "p",
        text:
          "Choosing curtains for a modern Dubai apartment can feel overwhelming. There are dozens of styles, hundreds of fabrics, and a lot of jargon. This guide cuts through the noise and shows you the curtain styles that actually work in Dubai apartments today — and which one to pick for each room.",
      },
      {
        type: "h2",
        text: "Why apartment curtains in Dubai are different",
      },
      {
        type: "p",
        text:
          "Most modern Dubai apartments share a few features: floor-to-ceiling glass, strong sunlight, high ceilings, and open-plan living. The curtains you choose have to handle heat, glare, and clean modern lines — all at once. The wrong style can make a beautiful living room feel heavy or dated.",
      },
      {
        type: "p",
        text:
          "Good apartment curtains do four jobs well: they soften light, protect privacy, hide the curtain hardware, and complement (rather than compete with) the architecture.",
      },
      {
        type: "h2",
        text: "The five curtain styles that work best in Dubai apartments",
      },
      {
        type: "h3",
        text: "1. Ripple fold curtains",
      },
      {
        type: "p",
        text:
          "Ripple fold is the most popular style for modern Dubai apartments — and for good reason. The fabric falls in soft, even waves on a slim track, giving you that clean, hotel-suite look without any pleats or hooks showing. It works beautifully with high ceilings and large windows.",
      },
      {
        type: "p",
        text:
          "Best for: living rooms, primary bedrooms, study rooms, and any space with floor-to-ceiling windows.",
      },
      {
        type: "h3",
        text: "2. Pinch pleat curtains",
      },
      {
        type: "p",
        text:
          "Pinch pleat is the more traditional cousin of ripple fold — the fabric is gathered at the top into neat pleats. It feels a bit more formal and shows craftsmanship in the stitching. Pinch pleat works well in classic-modern Dubai apartments where you still want a touch of structure.",
      },
      {
        type: "h3",
        text: "3. Sheer curtains",
      },
      {
        type: "p",
        text:
          "Sheers are the unsung heroes of Dubai apartments. They diffuse harsh midday sunlight beautifully, give you daytime privacy, and make a room feel bigger and lighter. Many clients pair sheers with a heavier curtain behind them — a setup we call double-layer.",
      },
      {
        type: "h3",
        text: "4. Blackout curtains",
      },
      {
        type: "p",
        text:
          "If you want to sleep in past sunrise (or you're managing a holiday home), blackout curtains are non-negotiable. Modern blackout fabrics are no longer the heavy, hotel-style ones — they come in lightweight linens and cottons that look great in residential interiors.",
      },
      {
        type: "h3",
        text: "5. Motorized curtains",
      },
      {
        type: "p",
        text:
          "For larger windows and high-up tracks, motorized curtains have become the default. A single Somfy-powered ripple fold curtain on a 4-metre window opens silently with a remote, app, or voice command — which makes them a favourite in smart-home apartments.",
      },
      {
        type: "h2",
        text: "Room-by-room picks",
      },
      {
        type: "ul",
        items: [
          "Living room: ripple fold or double-layer sheer + blackout.",
          "Master bedroom: blackout + sheer combo for full light control.",
          "Kids' room: motorized blackout (no dangling cords, child-safe).",
          "Home office: pinch pleat for structure or sheers for soft daylight.",
          "Dining area: sheers — for diffused daylight and an open feel.",
        ],
      },
      {
        type: "h2",
        text: "A few practical tips",
      },
      {
        type: "p",
        text:
          "Always factor in fabric drop. In Dubai apartments with 3+ metre ceilings, even a beautifully tailored curtain looks unfinished if it stops short. Your curtains should kiss the floor — or pool slightly, depending on the look you want.",
      },
      {
        type: "p",
        text:
          "Also: think about the track. Slim, recessed tracks always look more luxurious than visible curtain rods in modern apartments. If your apartment has a curtain pelmet or recess, plan for the track to disappear into it.",
      },
      {
        type: "h2",
        text: "Where to start",
      },
      {
        type: "p",
        text:
          "The honest answer is: book a measurement visit first. A good curtain company will measure your windows, see the light, and recommend styles that match your apartment — not the other way round.",
        link: {
          text: "Explore custom curtains in Dubai by Time Furniture",
          to: "/curtains-dubai",
        },
      },
    ],
  },

  {
    slug: "ripple-fold-vs-pinch-pleat-curtains",
    title: "Ripple Fold vs Pinch Pleat Curtains — What to Choose?",
    excerpt:
      "Both look stunning, but they hang differently, drape differently, and suit very different interiors. Here's how to choose between ripple fold and pinch pleat.",
    category: "Curtains",
    date: "2026-04-15",
    readTime: "5 min read",
    image: "/services/pinch-pleat-curtains-timefurniture.webp",
    imageAlt:
      "Pinch pleat curtains in a luxury Dubai villa by Time Furniture",
    metaTitle:
      "Ripple Fold vs Pinch Pleat Curtains — What to Choose? | Time Furniture",
    metaDescription:
      "A clear comparison between ripple fold and pinch pleat curtains — how they hang, where they work best, and which one suits your Dubai home.",
    sections: [
      {
        type: "p",
        text:
          "Ripple fold and pinch pleat are the two most asked-for curtain styles in Dubai right now. They're both beautiful — but they suit very different interiors. This guide breaks down the differences so you can choose with confidence.",
      },
      {
        type: "h2",
        text: "Quick definitions",
      },
      {
        type: "h3",
        text: "Ripple fold",
      },
      {
        type: "p",
        text:
          "Ripple fold is a modern style where the fabric falls in soft, evenly spaced S-curves on a slim, almost invisible track. There are no pleats, no hooks, and no headings — just clean, continuous waves of fabric. It looks effortless, but the engineering behind it is precise.",
      },
      {
        type: "h3",
        text: "Pinch pleat",
      },
      {
        type: "p",
        text:
          "Pinch pleat is a classic style where the fabric is gathered at the top into neat, structured pleats — usually two, three, or four folds per pleat. Each pleat is hand-stitched, giving the curtain a tailored, formal look from the top down.",
      },
      {
        type: "h2",
        text: "How they hang and drape",
      },
      {
        type: "p",
        text:
          "The biggest visual difference is at the top. Ripple fold has no defined heading — the fabric just starts rippling immediately, which feels modern and minimal. Pinch pleat has a clear top heading with sculpted pleats, which feels more traditional and crafted.",
      },
      {
        type: "p",
        text:
          "When closed, ripple fold drapes in continuous soft waves all the way down. Pinch pleat falls in deeper, more structured columns — almost like vertical channels. Both look luxurious; they just speak different design languages.",
      },
      {
        type: "h2",
        text: "Where each style works best",
      },
      {
        type: "h3",
        text: "Choose ripple fold if:",
      },
      {
        type: "ul",
        items: [
          "Your interior is modern, contemporary, or minimalist.",
          "You have floor-to-ceiling windows with high ceilings.",
          "You want curtains to feel almost invisible until they move.",
          "You're planning to motorize them — ripple fold and motors are made for each other.",
        ],
      },
      {
        type: "h3",
        text: "Choose pinch pleat if:",
      },
      {
        type: "ul",
        items: [
          "Your interior has classic, traditional, or transitional styling.",
          "You like seeing the craftsmanship — the pleats are part of the look.",
          "You want curtains that feel more formal and structured.",
          "You're using heavier fabrics like velvet or thick linen.",
        ],
      },
      {
        type: "h2",
        text: "Fabric thickness matters",
      },
      {
        type: "p",
        text:
          "Ripple fold needs a fabric with enough body to hold its waves — too thin and the curve collapses. Pinch pleat is more forgiving with fabric weight, since the pleats themselves carry the structure.",
      },
      {
        type: "h2",
        text: "Track and hardware differences",
      },
      {
        type: "p",
        text:
          "Ripple fold runs on a slim ceiling-mounted track that almost disappears. Pinch pleat usually hangs from a track or rod, with hooks. If you're after a clean, hotel-suite look in a modern apartment, ripple fold's hardware advantage matters.",
      },
      {
        type: "h2",
        text: "Cost and maintenance",
      },
      {
        type: "p",
        text:
          "Both styles cost roughly the same in fabric. Pinch pleat takes a little more stitching work because of the pleats, while ripple fold needs precise track hardware. In real-world Dubai pricing, the difference is small — your fabric choice will move the cost more than the style.",
      },
      {
        type: "p",
        text:
          "For maintenance, both are easy to vacuum and dry-clean. Ripple fold draws back into a tighter stack, which means slightly less obstruction at the window edge.",
      },
      {
        type: "h2",
        text: "Our honest take",
      },
      {
        type: "p",
        text:
          "If your home is modern, choose ripple fold. If your home is classical or you love the look of structured pleats, choose pinch pleat. There's no wrong answer — just the one that suits your space.",
        link: {
          text: "See both styles in our curtains and blinds collection",
          to: "/curtains-dubai",
        },
      },
    ],
  },

  {
    slug: "are-motorized-curtains-worth-it-in-dubai",
    title: "Are Motorized Curtains Worth It in Dubai?",
    excerpt:
      "Motorized curtains are everywhere in new Dubai homes — but are they actually worth the upgrade? Here's an honest take.",
    category: "Automation",
    date: "2026-04-08",
    readTime: "6 min read",
    image: "/services/M-Track-curtains-timefurniture.webp",
    imageAlt:
      "Somfy motorized M-track curtains in a Dubai smart home by Time Furniture",
    metaTitle:
      "Are Motorized Curtains Worth It in Dubai? | Time Furniture",
    metaDescription:
      "An honest look at motorized curtains in Dubai — the real benefits, when they're worth the upgrade, costs, and what to know before you commit.",
    sections: [
      {
        type: "p",
        text:
          "Motorized curtains have moved from a luxury upgrade to an almost-default in new Dubai homes. But are they actually worth it? After installing them in hundreds of apartments and villas across Dubai and Sharjah, here's our honest take.",
      },
      {
        type: "h2",
        text: "What are motorized curtains, exactly?",
      },
      {
        type: "p",
        text:
          "Motorized curtains run on a track with a small electric motor inside it. You open and close them with a remote, a wall switch, a smartphone app, or a voice command — Alexa, Google Home, or Apple HomeKit. The motor is hidden, silent, and drives the curtain smoothly across the full width of the window.",
      },
      {
        type: "p",
        text:
          "Most premium installations in the UAE use Somfy motors. Somfy is the global standard for curtain and blind automation, with reliable hardware, quiet operation, and full smart-home compatibility.",
      },
      {
        type: "h2",
        text: "When motorized curtains are clearly worth it",
      },
      {
        type: "h3",
        text: "1. Tall or wide windows",
      },
      {
        type: "p",
        text:
          "If your curtain track is over 4 metres wide or 3 metres tall, motorization is almost a necessity. Pulling that much fabric manually puts strain on the track, and the cord pulleys for tall windows are awkward at best.",
      },
      {
        type: "h3",
        text: "2. Hard-to-reach windows",
      },
      {
        type: "p",
        text:
          "Floor-to-ceiling glass behind a sofa, double-height living rooms, or windows above a staircase — these are spots where you simply can't reach the curtain comfortably. A remote solves it instantly.",
      },
      {
        type: "h3",
        text: "3. Multiple windows in one room",
      },
      {
        type: "p",
        text:
          "A modern Dubai living room often has 3, 4, or even 5 windows on the same wall. Opening and closing each one manually is a chore. Motorized curtains let you control all of them with a single tap.",
      },
      {
        type: "h3",
        text: "4. Smart-home integration",
      },
      {
        type: "p",
        text:
          "If you've already invested in lighting automation, climate control, or audio systems, adding motorized curtains is the obvious next step. You can set scenes — \"good morning\" opens the curtains and turns on the lights; \"movie night\" closes the blackouts and dims everything else.",
      },
      {
        type: "h3",
        text: "5. Holiday home rentals",
      },
      {
        type: "p",
        text:
          "For holiday home owners, motorized curtains are a small detail that elevates the guest experience. Guests love the novelty, and you avoid the wear-and-tear that manual pulling causes over time.",
      },
      {
        type: "h2",
        text: "When manual curtains are still the better choice",
      },
      {
        type: "p",
        text:
          "Not every window needs a motor. Smaller windows under 2 metres wide, kitchen windows that you open occasionally, and rooms where you barely close the curtains anyway — these don't need automation. Manual is cheaper, simpler, and just as elegant.",
      },
      {
        type: "h2",
        text: "What about the cost?",
      },
      {
        type: "p",
        text:
          "Motorization adds roughly 30–50% to the cost of a single curtain installation, depending on the motor model and whether you need wiring or battery-powered. The bigger the window, the more obvious the value — and the smaller the relative price difference.",
      },
      {
        type: "p",
        text:
          "We always recommend wired motors over battery motors for permanent installations. Wired motors last decades with zero maintenance; battery motors need recharging or replacement every couple of years.",
      },
      {
        type: "h2",
        text: "What to check before you commit",
      },
      {
        type: "ul",
        items: [
          "Is power available at the curtain track? If not, plan it during MEP works.",
          "Will it integrate with your smart home? Confirm Alexa / Google / HomeKit support.",
          "Who installs and services it? Choose a provider with their own technical team.",
          "What's the warranty? Reputable brands offer 5+ years on the motor.",
        ],
      },
      {
        type: "h2",
        text: "Our verdict",
      },
      {
        type: "p",
        text:
          "For large windows, smart homes, holiday rentals, and primary bedrooms — yes, motorized curtains are absolutely worth it. For small, easy-to-reach windows, manual still wins on simplicity. The smartest approach is to motorize where it genuinely improves daily life and stay manual where it doesn't.",
        link: {
          text: "Talk to our curtain team about motorized solutions",
          to: "/curtains-dubai#curtain-quote",
        },
      },
    ],
  },
];

export const getArticleBySlug = (slug) =>
  ARTICLES.find((a) => a.slug === slug);
